/**
 * The Enrichment tab's data (WEB-12814). The waterfall routes answer with the bare value, not the
 * `{ data }` envelope (api `controller/customer/prospectingTools.ts`).
 */

export type Provider = 'apollo' | 'hunter' | 'lusha';
export type Channel = 'email' | 'phoneNumber';
export type WaterfallMode = 'automatic' | 'custom';

export type ContactWaterfall = { mode: WaterfallMode } & Record<Channel, Provider[]>;

export type ProviderUsage = { provider: Provider; asked: number; found: number; creditsSek: number };
export type WaterfallUsage = { from: string; to: string; providers: ProviderUsage[] };

/** Whose key a provider runs on, as the server resolves it: `default` is Upsales' key, billed as AI credits. */
export type KeySource = 'client' | 'default' | 'none';

export const PROVIDERS: Provider[] = ['apollo', 'hunter', 'lusha'];
export const CHANNELS: Channel[] = ['email', 'phoneNumber'];

/** Brand names, shown as they are. */
export const PROVIDER_NAMES: Record<Provider, string> = { apollo: 'Apollo', hunter: 'Hunter', lusha: 'Lusha' };

export const CHANNEL_LABEL: Record<Channel, string> = {
	email: 'prospectingTools.enrichment.channel.email',
	phoneNumber: 'voice.phoneNumber'
};

/** Mirrors base `CAN_FIND` (peopleEnrichment.ts): the server drops any other provider from a channel. */
export const CAN_FIND: Record<Channel, Provider[]> = {
	email: ['apollo', 'hunter'],
	phoneNumber: ['apollo', 'hunter', 'lusha']
};

const WATERFALL_PATH = '/api/v2/prospectingTools/waterfall';
export const CONNECTIONS_SETTINGS_HREF = '#/ai-workspace/settings/connections';

const readJson = async <T>(res: Response): Promise<T> => {
	const body = await res.json();
	if (!res.ok || body?.error) {
		throw new Error(body?.error?.msg || `Request failed (${res.status})`);
	}
	return body as T;
};

export const fetchWaterfall = () => fetch(WATERFALL_PATH).then(res => readJson<ContactWaterfall>(res));

export const saveWaterfall = (waterfall: ContactWaterfall) =>
	fetch(WATERFALL_PATH, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(waterfall)
	}).then(res => readJson<ContactWaterfall>(res));

export const fetchWaterfallKeys = () =>
	fetch(`${WATERFALL_PATH}/keys`).then(res => readJson<Record<Provider, KeySource>>(res));

export const fetchWaterfallUsage = () => fetch(`${WATERFALL_PATH}/usage`).then(res => readJson<WaterfallUsage>(res));

export const sameOrder = (a: ContactWaterfall, b: ContactWaterfall) =>
	a.mode === b.mode && CHANNELS.every(channel => a[channel].join() === b[channel].join());

/** Whether Airscale answers first on this account (WEB-13395). The route exists only with AIRSCALE_INTEGRATION. */
const AIRSCALE_PATH = '/api/v2/prospectingTools/airscale';

export const fetchAirscale = () => fetch(AIRSCALE_PATH).then(res => readJson<{ enabled: boolean }>(res));

export const saveAirscale = (enabled: boolean) =>
	fetch(AIRSCALE_PATH, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ enabled })
	}).then(res => readJson<{ enabled: boolean }>(res));
